import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Reserva } from '../../../api/ops/reservas';
import { Icono } from '../../../componentes/Icono';
import { nombreDe, nombreRecursos, type ContextoReservas } from '../contexto';
import { diaLargo, hoyEn, horaEn } from '../fechas';
import { APAGADAS, mayuscula } from '../textos';

/**
 * «Imprimir» de 8.1 sin el bloque Plano y sala (G796): la lista del día agrupada por zona en la RV Hoja imprimible (la
 * misma pieza que la hoja de ocupación de la Sala), con la misma lista y los mismos datos de listReservations. Al abrirse
 * pone `rv-imprime` en `<html>` y deja fuera de la impresión todo lo demás (`data-rv-hoja-fuera`): al imprimir solo
 * queda la hoja. La Puerta lleva el chip de lo ya sabido o la casilla para marcar a mano.
 */
interface Props {
    dia: string;
    lista: readonly Reserva[];
    nombres: Readonly<Record<string, string>>;
    contexto: ContextoReservas;
    quien: string;
    onCerrar: () => void;
}

export function HojaDelDia({ dia, lista, nombres, contexto, quien, onCerrar }: Props) {
    const capa = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const raiz = document.documentElement;
        raiz.classList.add('rv-imprime');
        const fuera = [...document.body.children].filter((e) => e !== capa.current);
        for (const e of fuera) {
            e.setAttribute('data-rv-hoja-fuera', '');
        }
        const tecla = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onCerrar();
            }
        };
        window.addEventListener('keydown', tecla);
        return () => {
            raiz.classList.remove('rv-imprime');
            for (const e of fuera) {
                e.removeAttribute('data-rv-hoja-fuera');
            }
            window.removeEventListener('keydown', tecla);
        };
    }, [onCerrar]);

    const zonas = [...new Set(lista.map((r) => r.zone_id))].map((id) => ({
        id,
        nombre: contexto.zonas.find((z) => z.id === id)?.name ?? 'Zona',
        filas: lista.filter((r) => r.zone_id === id).sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    }));
    const ahora = new Date();
    const puerta = (r: Reserva) => {
        if (APAGADAS.includes(r.status)) {
            return <span className="rv-hoja-chip rv-hoja-chip--no">Cancelada</span>;
        }
        if (r.status === 'arrived' || r.status === 'completed') {
            return <span className="rv-hoja-chip">{r.arrived_at == null ? 'Llegó' : `Llegó ${horaEn(r.arrived_at, contexto.zona)}`}</span>;
        }
        if (r.status === 'no_show') {
            return <span className="rv-hoja-chip rv-hoja-chip--no">No vino</span>;
        }
        return <span className="rv-hoja-casilla" role="img" aria-label="Sin marcar: casilla para marcar a mano" />;
    };

    return createPortal(
        <div ref={capa} className="rv-hoja-fondo" role="dialog" aria-modal="true" aria-label="Reservas del día para imprimir">
            <div className="rv-hoja-barra">
                <span className="rv-hoja-barra-t">Imprimir las reservas</span>
                <button type="button" className="rv-b rv-b-sec" onClick={onCerrar}>
                    <Icono nombre="arrow-left" />
                    <span>Volver</span>
                </button>
                <button type="button" className="rv-b rv-b-pri" onClick={() => { window.print(); }}>
                    <Icono nombre="printer" />
                    <span>Imprimir o guardar en PDF</span>
                </button>
            </div>
            <article className="rv-hoja" aria-label={`Reservas del día, ${diaLargo(dia)}`}>
                <header className="rv-hoja-cab">
                    <div>
                        <h1 className="rv-hoja-titulo">Reservas del día</h1>
                        <span className="rv-hoja-sub">{mayuscula(diaLargo(dia))}</span>
                    </div>
                    <div className="rv-hoja-negocio"><b>{contexto.nombreNegocio}</b></div>
                </header>
                {zonas.length === 0 && <p className="rv-hoja-vacio">No hay reservas este día.</p>}
                {zonas.map((z) => {
                    const vivas = z.filas.filter((r) => !APAGADAS.includes(r.status));
                    const personas = vivas.reduce((n, r) => n + r.party_size, 0);
                    return (
                        <section key={z.id} className="rv-hoja-turno" aria-label={z.nombre}>
                            <div className="rv-hoja-banda">
                                <div><span className="rv-hoja-banda-t">{z.nombre}</span></div>
                                <div className="rv-hoja-banda-n">
                                    <span><b>{vivas.length}</b>{vivas.length === 1 ? ' reserva' : ' reservas'}</span>
                                    <span><b>{personas}</b>{personas === 1 ? ' persona' : ' personas'}</span>
                                </div>
                            </div>
                            <table className="rv-hoja-tabla">
                                <thead>
                                    <tr>
                                        <th scope="col" className="rv-hoja-hora">Hora</th>
                                        <th scope="col" className="rv-hoja-mesa">{mayuscula(contexto.palabra)}</th>
                                        <th scope="col" className="rv-hoja-cli">Cliente</th>
                                        <th scope="col" className="rv-hoja-pax">Pax</th>
                                        <th scope="col" className="rv-hoja-puerta">Puerta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {z.filas.map((r) => (
                                        <tr key={r.id} className={APAGADAS.includes(r.status) ? 'rv-hoja-anulada' : undefined}>
                                            <td className="rv-hoja-hora">{r.local_time ?? horaEn(r.starts_at, contexto.zona)}</td>
                                            <td className="rv-hoja-mesa">{nombreRecursos(contexto, r) || '—'}</td>
                                            <td className="rv-hoja-cli">
                                                <b>{nombreDe(r, nombres)}</b>
                                                {r.customer_note != null && r.customer_note !== '' && <span className="rv-hoja-notatxt">{r.customer_note}</span>}
                                            </td>
                                            <td className="rv-hoja-pax">
                                                {r.party_size}
                                                <span className="rv-hoja-solo-movil">{r.party_size === 1 ? ' persona' : ' personas'}</span>
                                            </td>
                                            <td className="rv-hoja-puerta">{puerta(r)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    );
                })}
                <footer className="rv-hoja-pie">
                    <span>{`${contexto.nombreNegocio} · ${mayuscula(diaLargo(dia))}`}</span>
                    <span>{`Impreso el ${diaLargo(hoyEn(contexto.zona, ahora))} a las ${horaEn(ahora, contexto.zona)} por ${quien}`}</span>
                </footer>
            </article>
        </div>,
        document.body,
    );
}
